import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const brandInputVariants = cva(
	"flex h-10 w-full border bg-transparent px-4 py-2 text-base text-foreground transition-colors file:border-0 file:bg-transparent file:text-base file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/40 focus-visible:border-[#6B3FF2] disabled:cursor-not-allowed disabled:opacity-50",
	{
		variants: {
			variant: {
				default: "border-border",
				error: "border-destructive focus-visible:ring-destructive/40",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

export interface BrandInputProps
	extends React.InputHTMLAttributes<HTMLInputElement>,
		VariantProps<typeof brandInputVariants> {
	icon?: React.ReactNode;
}

const BrandInput = React.forwardRef<HTMLInputElement, BrandInputProps>(
	({ className, variant, icon, type, ...props }, ref) => {
		if (icon) {
			return (
				<div className="relative">
					<div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true">
						{icon}
					</div>
					<input
						type={type}
						className={cn(brandInputVariants({ variant }), "pl-10", className)}
						ref={ref}
						{...props}
					/>
				</div>
			);
		}

		return (
			<input
				type={type}
				className={cn(brandInputVariants({ variant, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);
BrandInput.displayName = "BrandInput";

export interface BrandInputGroupProps
	extends React.HTMLAttributes<HTMLDivElement> {
	label?: string;
	error?: string;
	required?: boolean;
}

const BrandInputGroup = React.forwardRef<HTMLDivElement, BrandInputGroupProps>(
	({ className, label, error, required, children, ...props }, ref) => {
		const generatedId = React.useId();
		const errorId = `${generatedId}-error`;

		// Determine if the first child already has an explicit id
		const firstChild = React.Children.toArray(children)[0];
		const existingId =
			React.isValidElement(firstChild) &&
			(firstChild.props as Record<string, unknown>).id;
		const inputId = (existingId as string | undefined) ?? generatedId;

		// Inject id + aria-describedby into the first child if not already set
		const enhancedChildren = React.Children.map(children, (child, index) => {
			if (index !== 0 || !React.isValidElement(child)) return child;
			const extra: Record<string, unknown> = {};
			if (!existingId) extra.id = generatedId;
			if (error) {
				const existing = (child.props as Record<string, unknown>)[
					"aria-describedby"
				] as string | undefined;
				extra["aria-describedby"] = existing
					? `${existing} ${errorId}`
					: errorId;
				extra["aria-invalid"] = true;
			}
			return React.cloneElement(
				child as React.ReactElement<Record<string, unknown>>,
				extra,
			);
		});

		return (
			<div
				ref={ref}
				className={cn("flex flex-col gap-1.5", className)}
				{...props}
			>
				{label && (
					<label htmlFor={inputId} className="text-xs font-bold uppercase tracking-wide" style={{ fontFamily: "inherit", color: "var(--km-on-surface-muted)" }}>
						{label}
						{required && <span aria-hidden="true" className="text-destructive ml-0.5">*</span>}
						{required && <span className="sr-only">(required)</span>}
					</label>
				)}
				{enhancedChildren}
				{error && (
					<p id={errorId} role="alert" className="text-xs font-bold" style={{ color: "#ff6e84" }}>
						{error}
					</p>
				)}
			</div>
		);
	},
);
BrandInputGroup.displayName = "BrandInputGroup";

export { BrandInput, BrandInputGroup, brandInputVariants };
